import { fnUrl } from '@/lib/functionsBase';

export interface AIOrchestratorResponse {
  voice_reply: string;
  ui_action: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parameters: Record<string, any>;
}

// The paid Gemini API key lives server-side in the geminiProxy Cloud Function.
// The client calls the proxy so (a) the key never ships in the browser bundle and
// (b) browser/WebView requests aren't 403-blocked (server-to-server calls work).
const GEMINI_PROXY_URL = fnUrl('geminiProxy');

function buildSystemPrompt(context?: { role?: string; lang?: string }): string {
  return `
Ты — умный голосовой и текстовый ассистент платформы YofiPlace (маркетплейс бьюти-услуг в Израиле). Твоя задача — помогать клиентам находить мастеров и бронировать очереди, а мастерам — управлять своим расписанием.

Тон и Язык:
Ты общаешься вежливо, коротко и по делу. Никаких длинных вступлений.
Ты автоматически распознаешь язык запроса (Иврит, Русский, Английский, Французский) и отвечаешь на том же языке. (Текущий язык интерфейса пользователя: ${context?.lang || 'ru'})

Мультиязычность имен: Ты обязан применять транслитерацию. Если клиент ищет "Наталья", ищи "Natalia", "Natali", "נטליה".

Безопасность и Ограничения (CRITICAL RULES):
1. Никаких галлюцинаций: Ты не имеешь права выдумывать свободные часы или мастеров. Ты только извлекаешь параметры из слов пользователя, передаешь их в систему и озвучиваешь то, что вернула база данных.
2. Подтверждение действий (Master): Любые деструктивные или массовые действия (отмена дня, рассылка уведомлений, создание скидок) требуют явного подтверждения. Сначала сформируй текст сообщения, покажи его мастеру и спроси: "Отправляем?".
3. Математика времени (Client): При запросе нескольких услуг, объединяй их длительность.

Контекст пользователя:
Текущая роль: ${context?.role || 'client'} (client - обычный пользователь; owner - мастер; admin - администратор)

Формат вывода:
Твой ответ ВСЕГДА должен быть строго в формате JSON, без маркдаун-блоков:
{
  "voice_reply": "Текст, который ИИ скажет пользователю голосом",
  "ui_action": "search_masters | book_appointment | cancel_day | create_promo | unknown",
  "parameters": {
    "service": "услуга",
    "date": "дата в ISO",
    "time_start": "время",
    "time_end": "время",
    "location": "город",
    "language_filter": "язык",
    "master_name": "имя мастера"
  }
}
  `.trim();
}

export async function processAICommand(
  transcript: string,
  context?: { role?: string; lang?: string }
): Promise<AIOrchestratorResponse | null> {

  const systemPrompt = buildSystemPrompt(context);

  try {
    const res = await fetch(GEMINI_PROXY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        systemInstruction: {
          parts: [{ text: systemPrompt }]
        },
        contents: [
          {
            parts: [{ text: `User phrase: "${transcript}"` }]
          }
        ],
        generationConfig: {
          temperature: 0.2,
          responseMimeType: "application/json",
        }
      })
    });

    if (!res.ok) {
      console.error('Failed to parse with Gemini API:', res.statusText);
      return null;
    }

    const data = await res.json();
    const textResult = data.candidates?.[0]?.content?.parts?.[0]?.text;
    
    if (textResult) {
      const parsed = JSON.parse(textResult);
      return parsed as AIOrchestratorResponse;
    }
    
    return null;
  } catch (error) {
    console.error('Error during Gemini API call:', error);
    return null;
  }
}

/**
 * Process audio blob through Gemini API (multimodal).
 * Used as a fallback for WebView environments where Web Speech API is unavailable.
 * Gemini transcribes the audio AND processes the AI command in one step.
 */
export async function processAudioCommand(
  audioBlob: Blob,
  context?: { role?: string; lang?: string }
): Promise<AIOrchestratorResponse | null> {

  const systemPrompt = buildSystemPrompt(context);

  try {
    // Convert audio blob to base64
    const arrayBuffer = await audioBlob.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    let binary = '';
    for (let i = 0; i < uint8Array.length; i++) {
      binary += String.fromCharCode(uint8Array[i]);
    }
    const base64Audio = btoa(binary);

    // Determine MIME type
    const mimeType = audioBlob.type || 'audio/webm';

    const res = await fetch(GEMINI_PROXY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        systemInstruction: {
          parts: [{ text: systemPrompt + '\n\nThe user has sent a voice message. First transcribe what the user said, then process the command.' }]
        },
        contents: [
          {
            parts: [
              {
                inlineData: {
                  mimeType: mimeType,
                  data: base64Audio,
                }
              },
              {
                text: 'Transcribe the audio and process the user\'s request.'
              }
            ]
          }
        ],
        generationConfig: {
          temperature: 0.2,
          responseMimeType: "application/json",
        }
      })
    });

    if (!res.ok) {
      console.error('Failed to process audio with Gemini API:', res.statusText);
      return null;
    }

    const data = await res.json();
    const textResult = data.candidates?.[0]?.content?.parts?.[0]?.text;

    if (textResult) {
      const parsed = JSON.parse(textResult);
      return parsed as AIOrchestratorResponse;
    }

    return null;
  } catch (error) {
    console.error('Error during Gemini audio API call:', error);
    return null;
  }
}
